import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
// Import Shared Components
import { Loader, PasswordRequirements, Input } from 'shared-components';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Shared Validations
import { verifyRegisterValidations } from 'shared-components/src/validations/cardholderValidations';
// Import Shared Utils
import { parseURLSearchParams } from 'shared-components/src/utils/string';
// Import Global Components
import InfoTooltip from '../../../components/InfoTooltip';
// Import Global Layouts
import InputGroup from '../../../layouts/InputGroup';
// Import Local Layouts
import AuthLayout, { BareAuthLayout } from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
// Import Local Components
import InvalidLink from '../components/InvalidLink';
// Import Component Styles
import { FieldError, FormGroup, PasswordFieldWrapper } from '../styles';
// Import Utils
import { resetPassword, validateToken } from '../../../utils/auth';
import {
  validatePassword,
  checkForValidationErrors,
  validateConfirmPassword,
} from '../../../utils/validationHandlers';
import PwdEyeButton from './components/PwdEyeButton';

const ResetPassword = ({ history }) => {
  // Set States
  const initialresetPasswordInfo = {
    username: '',
    password: '',
    passwordConfirmation: '',
  };
  const initialValidationErrors = {
    username: '',
    password: '',
    passwordConfirmation: '',
  };
  const [formError, setFormError] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardholder, setCardholder] = useState({});
  const [passwordType, setPasswordType] = useState('password');
  const [confirmPasswordType, setConfirmPasswordType] = useState('password');

  // create intl instance
  const intl = useIntl();

  // Create reducer with initial card information state
  // Returns the updated state to be passed to components and a function to update the state
  const [resetPasswordInfo, setResetPasswordInfo] = useUpdateStateObject(
    initialresetPasswordInfo
  );
  const [validationErrors, setValidationErrors] = useUpdateStateObject(
    initialValidationErrors
  );

  // Validate token on page load
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const handleValidateToken = async () => {
      const { token } = parseURLSearchParams(window.location.search);

      if (!token) {
        setError(true);
        setLoading(false);
      }

      try {
        setLoading(true);
        const res = await validateToken(token);
        setCardholder({ ...res, token });
        setResetPasswordInfo({ key: 'username', value: res.username });
        setLoading(false);
      } catch (e) {
        setError(true);
      }
    };

    handleValidateToken();
  }, []);

  if (error) return <InvalidLink />;
  if (loading)
    return (
      <BareAuthLayout>
        <Loader />
      </BareAuthLayout>
    );

  const checkFormValidation = () => {
    const errors = validationErrors;

    // check username
    if (!verifyRegisterValidations.username(resetPasswordInfo.username)) {
      errors.username =
        resetPasswordInfo.username === ''
          ? 'Username is required'
          : 'Invalid username, please check and try again.';
    }

    // check password
    if (!verifyRegisterValidations.password(resetPasswordInfo.password)) {
      errors.password =
        resetPasswordInfo.password === ''
          ? 'Password is required'
          : 'Invalid password, please check and try again.';
    }

    // check password confirmation
    if (
      resetPasswordInfo.password !== resetPasswordInfo.passwordConfirmation ||
      resetPasswordInfo.passwordConfirmation === ''
    ) {
      errors.passwordConfirmation =
        resetPasswordInfo.passwordConfirmation === ''
          ? 'Password confirmation is required'
          : 'Password and confirmation do not match.';
    }

    setValidationErrors(errors);

    // Throws a generic error message if any of the fields are not valid
    const isInValid = Object.keys(validationErrors).find(field => {
      return validationErrors[field];
    });

    return isInValid;
  };

  const handleApiError = error => {
    let fieldName = '';

    switch (error.code) {
    case 'username_not_available':
      fieldName = 'username';
      break;
    case 'invalid_password':
      fieldName = 'password';
      break;
    default:
      break;
    }

    if (fieldName) {
      setValidationErrors({
        key: fieldName,
        value:
          error.code === 'card_not_activated'
            ? 'card_not_activated'
            : error.message,
      });
    } else {
      setFormError(error.message);
    }
  };

  const handleResetPassword = async payload => {
    try {
      setLoading(true);
      setFormError(false);
      await resetPassword(payload);
      setResetPasswordInfo({ key: 'username', value: '' });
      setResetPasswordInfo({ key: 'password', value: '' });
      setResetPasswordInfo({ key: 'passwordConfirmation', value: '' });
      setValidationErrors({ key: 'username', value: '' });
      setValidationErrors({ key: 'password', value: '' });
      setValidationErrors({ key: 'passwordConfirmation', value: '' });
      history.push('/login', {
        success: 'password-reset-success-alert',
      });
    } catch (e) {
      handleApiError(e);
    }
  };

  // Set Handlers
  // eslint-disable-next-line consistent-return
  const handleSubmit = async () => {
    // Check if password meets requirements
    if (checkFormValidation()) return;

    // Create payload to be sent
    const payload = {
      token: cardholder.token,
      username: cardholder.username,
      password: resetPasswordInfo.password,
      password_confirmation: resetPasswordInfo.passwordConfirmation,
    };

    handleResetPassword(payload);
    setLoading(false);
  };

  const handleRegisterKeyUp = async e => {
    if (e.keyCode === 13) {
      await handleSubmit();
    }
  };

  const validatePasswordRules = (password = '') => {
    const rules = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$*]/.test(password),
    };

    const isValid = Object.values(rules).every(Boolean);

    return { rules, isValid };
  };

  const { rules } = validatePasswordRules(resetPasswordInfo.password);


  const page = 'reset-password';

  // TODO: Need to get correct wording for this page from Operations
  return (
    <AuthLayout
      page={page}
      heading={intl.messages[`${page}-heading`]}
      subHeader={intl.messages[`${page}-subheader-text`]}
      error={formError}
    >
      <AuthForm
        page={page}
        loading={loading}
        onKeyUp={handleRegisterKeyUp}
        onSubmit={e => {
          e.preventDefault();
          checkForValidationErrors(validationErrors, setFormError);
          handleSubmit();
        }}
      >
        <FormGroup>
          <InputGroup
            label={intl.messages['auth-username-label']}
            labelFor="username"
            page="auth"
            required
          >
            <Input className={`${resetPasswordInfo.username ? 'is-disabled' : ''}`} disabled value={resetPasswordInfo.username} />
            {validationErrors.username && (
              <FieldError className="invalid-feedback">
                {validationErrors.username}
              </FieldError>
            )}
          </InputGroup>
          <InputGroup
            tooltip={(
              <InfoTooltip
                position="top"
                content={<PasswordRequirements messages={intl.messages} />}
              />
            )}
            label={intl.messages['auth-new-password-label']}
            labelFor="new-password"
            page="auth"
          >
            <PasswordFieldWrapper>
              <Input
                className={`${validationErrors.password ? 'is-invalid' : ''}`}
                value={resetPasswordInfo.password}
                error={validationErrors.password}
                onBlur={() =>
                  validatePassword(setValidationErrors, resetPasswordInfo)}
                onChange={e =>
                  setResetPasswordInfo({
                    key: 'password',
                    value: e.target.value.trim(),
                  })}
                type={passwordType}
              />
              <PwdEyeButton
                type={passwordType}
                settype={setPasswordType}
              />
            </PasswordFieldWrapper>
            {validationErrors.password && (
              <FieldError className="invalid-feedback">
                {validationErrors.password}
                {resetPasswordInfo.password && (
                  <div>
                    <p className={rules.minLength ? 'valid' : 'invalid'}>
                      • Password must be longer than 8 characters
                    </p>
                    <p className={rules.hasUppercase ? 'valid' : 'invalid'}>
                      • Must have one capital letter
                    </p>
                    <p className={rules.hasNumber ? 'valid' : 'invalid'}>
                      • Must contain at least one number
                    </p>
                    <p className={rules.hasSymbol ? 'valid' : 'invalid'}>
                      • Must contain one symbol (ex: !@#$*)
                    </p>
                  </div>
                )}
              </FieldError>
            )}
          </InputGroup>
          <InputGroup
            label={intl.messages['auth-password-confirmation-label']}
            labelFor="password-confirmation"
            page="auth"
            required
          >
            <PasswordFieldWrapper>
              <Input
                className={`${
                  validationErrors.passwordConfirmation ? 'is-invalid' : ''
                }`}
                value={resetPasswordInfo.passwordConfirmation}
                error={validationErrors.passwordConfirmation}
                onBlur={e => {
                  if (e.target.value.trim()) {
                    validateConfirmPassword(setValidationErrors, resetPasswordInfo);
                  } else {
                    setValidationErrors({
                      key: 'passwordConfirmation',
                      value: 'Password confirmation is required',
                    });
                  }
                }}
                onChange={e =>
                  setResetPasswordInfo({
                    key: 'passwordConfirmation',
                    value: e.target.value.trim(),
                  })}
                onPaste={e => {
                  // Prevent pasting
                  e.preventDefault();
                }}
                type={confirmPasswordType}
              />
              <PwdEyeButton
                type={confirmPasswordType}
                settype={setConfirmPasswordType}
              />
            </PasswordFieldWrapper>
            {validationErrors.passwordConfirmation && (
              <FieldError className="invalid-feedback">
                {validationErrors.passwordConfirmation}
              </FieldError>
            )}
          </InputGroup>
        </FormGroup>
      </AuthForm>
    </AuthLayout>
  );
};

ResetPassword.propTypes = {
  history: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default ResetPassword;
